import { useState, useEffect } from 'react';

import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Header,
  NonCancelableCustomEvent,
  Select,
  SelectProps,
  SpaceBetween,
  Textarea,
  Tiles,
  TilesProps,
} from '@amzn/awsui-components-react';
import type { MultiselectProps } from '@amzn/awsui-components-react/polaris/multiselect';
import {
  Event,
  GetParticipationsByEventAndAttendeesCommandOutput,
  IdentifierType,
  Shift,
  ShiftType,
} from '@amzn/red-velvet-api';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  convertToBulkParticipantData,
  useBulkParticipantSessionStorage,
} from '../../../hooks/attendance/bulkParticipantSessionStorage';
import { getEnabledFeatures, useFeatures } from '../../../hooks/features';
import { callQueryClientSingletonRedVelvetQuery } from '../../../hooks/singleItemRedVelvetQuery';
import { useUser } from '../../../hooks/user';
import { SearchTable } from '../wizard/searchTable';
import { publishKatalMetric } from '../../katalAnalytics';

const MAX_BULK_ADD_ENTRIES = 1000;
export type ModalTileOption = 'search' | 'bulk';

const DEFAULT_IDENTIFIER_TYPE_VALUE = IdentifierType.ALIAS;

interface BulkUploadAddAttendeesContentProps {
  event: Event;
  shiftId?: string;
  shifts: Shift[];
  isModalOpen: boolean;
  onCancel: () => void;
  onValidationSuccess: (
    data: GetParticipationsByEventAndAttendeesCommandOutput,
    identifierType: IdentifierType,
    tileOption: ModalTileOption
  ) => void;
}

export function BulkUploadAddAttendeesContent({
  event,
  shiftId,
  shifts,
  isModalOpen,
  onCancel,
  onValidationSuccess,
}: BulkUploadAddAttendeesContentProps) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useTranslation(namespaces);
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const queryClient = useQueryClient();
  const { bulkParticipants, updateBulkParticipants } = useBulkParticipantSessionStorage();
  const user = useUser();

  const [selectedTileValue, setSelectedTileValue] = useState<ModalTileOption>('search');
  const [identifierTypeValue, setIdentifierTypeValue] = useState<IdentifierType>(
    DEFAULT_IDENTIFIER_TYPE_VALUE
  );
  const [bulkAttendeesList, setBulkAttendeesList] = useState('');
  const [errors, setErrors] = useState<{
    bulkAttendeesList?: string;
    otherErrors?: string;
  }>({});
  const [isValidating, setIsValidating] = useState(false);

  const [searchedAttendeesList, setSearchedAttendeesList] = useState<string[]>([]);

  const identifierType: SelectProps.Option & { value: IdentifierType } = {
    label: t('bulkUpload.bulkAdd.identifierOptions.emailOrAlias'),
    value: identifierTypeValue,
  };

  // Build shift options for the multiselect dropdown (exclude WAITLIST shifts)
  const shiftOptions: MultiselectProps.Options = shifts
    .filter((shift) => shift.shiftType !== ShiftType.WAITLIST)
    .map((shift: Shift) => ({
      label: shift.name,
      value: shift.shiftId,
    }));

  // Reset state when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      setSelectedTileValue('search');
      setIdentifierTypeValue(DEFAULT_IDENTIFIER_TYPE_VALUE);
      setBulkAttendeesList('');
      setErrors({});
      setIsValidating(false);
      setSearchedAttendeesList([]);
    }
  }, [isModalOpen]);

  // Refresh shifts if DROP_IN was created and not in current list
  const refreshShiftsIfDropInCreated = (dropInShiftId?: string) => {
    const dropInMissing = dropInShiftId && !shifts.some((s) => s.shiftId === dropInShiftId);
    if (dropInMissing) {
      queryClient.refetchQueries({
        queryKey: ['RedVelvetApi', 'getShiftsByEvent', { eventId: event?.eventId }],
      });
    }
  };

  const tileOptions = [
    {
      description: t('bulkUpload.tiles.search.description'),
      label: t('bulkUpload.tiles.search.label'),
      value: 'search',
    },
    {
      description: t('bulkUpload.tiles.bulk.description'),
      label: t('bulkUpload.tiles.bulk.label'),
      value: 'bulk',
    },
  ];

  const identifierOptions = [
    {
      label: t('bulkUpload.bulkAdd.identifierOptions.emailOrAlias'),
      value: IdentifierType.ALIAS,
    },
    {
      label: t('bulkUpload.bulkAdd.identifierOptions.employee'),
      value: IdentifierType.EMPLOYEE_ID,
    },
    {
      disabled: true,
      disabledReason: t('bulkUpload.bulkAdd.identifierOptions.badgeDisabledReason'),
      label: t('bulkUpload.bulkAdd.identifierOptions.badge'),
      value: IdentifierType.BADGE_ID,
    },
  ];

  const handleTileChange = ({
    detail,
  }: NonCancelableCustomEvent<TilesProps.ChangeDetail>): void => {
    setSelectedTileValue(detail.value as ModalTileOption);
    if (errors.otherErrors) {
      setErrors({ ...errors, otherErrors: undefined });
    }
  };

  const handleIdentifierChange = ({
    detail,
  }: NonCancelableCustomEvent<SelectProps.ChangeDetail>): void => {
    const selected = detail.selectedOption as SelectProps.Option & { value: IdentifierType };
    setIdentifierTypeValue(selected.value);
  };

  const handleAttendeesChange = ({ detail }: NonCancelableCustomEvent<{ value: string }>): void => {
    setBulkAttendeesList(detail.value);

    if (errors.bulkAttendeesList) {
      setErrors({ ...errors, bulkAttendeesList: '' });
    }
  };

  const handleBulkAddValidate = async (): Promise<void> => {
    const newErrors: { bulkAttendeesList?: string } = {};

    if (!bulkAttendeesList.trim()) {
      newErrors.bulkAttendeesList = t('bulkUpload.bulkAdd.errors.empty');
      setErrors(newErrors);
      return;
    }

    const entries = bulkAttendeesList
      .split(/[\t,\n]+/)
      .map((entry) => {
        const trimmed = entry.trim();
        const atIndex = trimmed.indexOf('@');
        return atIndex !== -1 ? trimmed.substring(0, atIndex) : trimmed;
      })
      .filter((entry) => entry.length > 0);

    if (entries.length === 0) {
      newErrors.bulkAttendeesList = t('bulkUpload.bulkAdd.errors.empty');
      setErrors(newErrors);
      return;
    }

    if (entries.length > MAX_BULK_ADD_ENTRIES) {
      newErrors.bulkAttendeesList = t('bulkUpload.bulkAdd.errors.tooMany');
      setErrors(newErrors);
      return;
    }

    setIsValidating(true);

    try {
      const response = await callQueryClientSingletonRedVelvetQuery(
        queryClient,
        {
          redVelvetQueryKey: [
            'getParticipationsByEventAndAttendees',
            {
              eventId: event?.eventId,
              shiftId: shiftId,
              attendees: entries.join(','),
              attendeeIdentifierType: identifierType.value,
              shiftEndedBefore: event?.eventId ? new Date() : undefined,
            },
          ],
        },
        getEnabledFeatures(features, searchParams)
      );

      // Refresh shifts if DROP_IN was created and not in current list
      refreshShiftsIfDropInCreated(response.dropInShiftId);

      // Track user added entries with identifier type
      publishKatalMetric(
        `attendance_bulkUploadBulkAddEntriesCount_${identifierType.value}`,
        entries.length.toString()
      );

      // Track validation errors with identifier type
      if (response.failedIdentifiers && response.failedIdentifiers.length > 0) {
        const failedCount = response.failedIdentifiers.length.toString();
        publishKatalMetric(
          `attendance_bulkUploadValidationErrorsCount_${identifierType.value}`,
          failedCount
        );
      }

      onValidationSuccess(response, identifierType.value, selectedTileValue);
    } catch (error) {
      console.error('Error validating attendees:', error);
      setErrors({ bulkAttendeesList: t('bulkUpload.bulkAdd.errors.failedConversion') });
    } finally {
      setIsValidating(false);
    }
  };

  const handleNextStepOnSearchAttendee = async (): Promise<void> => {
    setIsValidating(true);

    try {
      const isEventLevelTracking = !shiftId && event?.eventId;

      const response = await callQueryClientSingletonRedVelvetQuery(
        queryClient,
        {
          redVelvetQueryKey: [
            'getParticipationsByEventAndAttendees',
            {
              eventId: event?.eventId,
              shiftId,
              attendees: searchedAttendeesList.join(','),
              attendeeIdentifierType: IdentifierType.ALIAS,
              shiftEndedBefore: event?.eventId ? new Date() : undefined,
            },
          ],
        },
        getEnabledFeatures(features, searchParams)
      );

      // Refresh shifts if DROP_IN was created and not in current list
      refreshShiftsIfDropInCreated(response.dropInShiftId);

      // Store bulk participants to session storage
      // Single-shift: Convert API response to bulk format with 'alias' as key
      // Event-level: Data already saved to bulk-participants by SearchTable when user selected shifts
      //   - Individual shift selector saves on each change (buildBulkUploadColumnMap)
      //   - Bulk shift selector saves when "Select shifts for X selected" is used
      //   - Both use composite keys 'alias-shiftId' for event-level tracking
      if (!isEventLevelTracking) {
        const bulkParticipantData = convertToBulkParticipantData(
          response.participations || [],
          response.peopleWithoutParticipation || [],
          user.userId
        );
        updateBulkParticipants(bulkParticipantData);
      }

      // Track user added entries with identifier type
      publishKatalMetric(
        `attendance_bulkUploadSearchAddEntriesCount_${IdentifierType.ALIAS}`,
        searchedAttendeesList.length.toString()
      );
      onValidationSuccess(response, IdentifierType.ALIAS, selectedTileValue);
    } catch (error) {
      console.error('Error getting participations:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setErrors({ otherErrors: errorMessage });
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="bulk-upload-modal-content">
      <Tiles
        columns={2}
        items={tileOptions}
        value={selectedTileValue}
        onChange={handleTileChange}
      />

      {errors.otherErrors && (
        <Box margin={{ top: 's' }}>
          <Alert
            type="error"
            header={t('bulkUpload.validation.unknownFailureHeader')}
            data-testid="alert"
          >
            {errors.otherErrors}
            <br />
            {t('bulkUpload.validation.unknownFailureContent')}
          </Alert>
        </Box>
      )}

      <div className="bulk-upload-modal-scrollable">
        <SpaceBetween size="l">
          {selectedTileValue === 'search' && (
            <SearchTable
              key={isModalOpen ? 'open' : 'closed'}
              participants={[]}
              setParticipants={() => []}
              useNonReactive={true}
              mode="bulkUpload"
              shiftOptions={shiftOptions}
              isUnlistedEventMode={!event?.eventId && shifts.length === 0}
              event={event}
              shiftIdFromTrackAttendanceWizard={shiftId}
              onUpdate={() => {
                const attendees = JSON.parse(sessionStorage.getItem('attendees') || '[]');
                setSearchedAttendeesList(attendees);
              }}
            />
          )}
          {selectedTileValue === 'bulk' && (
            <Container header={<Header variant="h2">{t('bulkUpload.bulkAdd.header')}</Header>}>
              <SpaceBetween size="l">
                <FormField label={t('bulkUpload.bulkAdd.identifierLabel')}>
                  <Select
                    options={identifierOptions}
                    placeholder={t('bulkUpload.bulkAdd.identifierPlaceholder')}
                    selectedOption={identifierType}
                    onChange={handleIdentifierChange}
                  />
                </FormField>
                <FormField
                  constraintText={
                    <>
                      • {t('bulkUpload.bulkAdd.constraints.duplicates')}
                      <br />• {t('bulkUpload.bulkAdd.constraints.tracked')}
                      <br />• {t('bulkUpload.bulkAdd.constraints.maximum')}
                    </>
                  }
                  label={t('bulkUpload.bulkAdd.listLabel')}
                  errorText={errors.bulkAttendeesList}
                  stretch
                >
                  <Textarea
                    placeholder={t('bulkUpload.bulkAdd.listPlaceholder')}
                    rows={8}
                    value={bulkAttendeesList}
                    onChange={handleAttendeesChange}
                  />
                </FormField>
              </SpaceBetween>
            </Container>
          )}
        </SpaceBetween>
      </div>

      <Box padding={{ top: 'l' }}>
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancel}>
              {t('bulkUpload.buttons.cancel')}
            </Button>
            {selectedTileValue === 'search' && (
              <Button
                disabled={
                  searchedAttendeesList.length === 0 && Object.keys(bulkParticipants).length === 0
                }
                variant="primary"
                onClick={() => handleNextStepOnSearchAttendee()}
                loading={isValidating}
              >
                {errors.otherErrors ? t('bulkUpload.buttons.retry') : t('bulkUpload.buttons.done')}
              </Button>
            )}
            {selectedTileValue === 'bulk' && (
              <Button
                variant={'primary'}
                onClick={() => handleBulkAddValidate()}
                loading={isValidating}
              >
                {errors.otherErrors
                  ? t('bulkUpload.buttons.retry')
                  : t('bulkUpload.buttons.validate')}
              </Button>
            )}
          </SpaceBetween>
        </Box>
      </Box>
    </div>
  );
}
